/* eslint-disable react-hooks/exhaustive-deps */
import { showAddOrUpdateServiceState } from "@/atoms";
import axios from "@/axios.config.ts";
import CustomInput from "@/components/common/inputs/CustomInput.tsx";
import MainModal from "@/components/modals/MainModal.tsx";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useGetAllCouncils } from "@/services/councils";
import { useCreateService, useGetServicesByCouncil, useUpdateService } from "@/services/services";
import { EServiceStatus, ServiceDependencyGroup, DependencyOperator } from "@/types";
import { Button, Checkbox, MultiSelect, Select, ActionIcon, Paper, Text, Stack, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { convertLegacyDependencies } from "@/utils/serviceDependencies";


const AddUpdateService = () => {
  const [show, setShow] = useRecoilState(showAddOrUpdateServiceState);

  const { user } = useAuth();
  const council = useUserCouncil();
  const [data, setData] = useState({
    name: "",
    registrationFee: 0,
    council_id: null as string | null,
    approvalFinalLevel: 0,
    code: "",
    productCode: "",
    status: EServiceStatus.ACTIVE,
    dependencyGroups: [] as ServiceDependencyGroup[],
    renewable: false,
    baseServiceId: null as string | null,
  });
  const [roleApproval, setRoleApproval] = useState<any[]>([]);
  const {
    mutate: createService,
    isPending: creatingService
  } = useCreateService();
  const {
    mutate: updateService,
    isPending: updatingService
  } = useUpdateService();

  const queryClient = useQueryClient();

  const {
    data: servicesByCouncil,
  } = useGetServicesByCouncil({
    councilId: data.council_id || undefined,
    enabled: !!data.council_id,
  });
  const {
    data: tblcouncils,
  } = useGetAllCouncils();

  const fetchLevels = async (council_id: string) => {
    try {
      const response = await axios.get(
        `/approvalstructurelevel/council/${council_id}`
      );
      if (Array.isArray(response.data)) {
        const formattedData = response.data.map((item: any) => ({
          value: String(item.approvalLevel),
          label: `${item.role.roleName} - Level ${item.approvalLevel}`,
        }));
        setRoleApproval(formattedData);
      } else {
        setRoleApproval([]);
      }
    } catch (error) {
      console.error("Error fetching roles levels:", error);
      setRoleApproval([]);
    }
  };

  const addDependencyGroup = () => {
    setData({
      ...data,
      dependencyGroups: [
        ...data.dependencyGroups,
        {
          operator: "AND" as DependencyOperator,
          dependentServiceIds: [],
        },
      ],
    });
  };

  const removeDependencyGroup = (index: number) => {
    setData({
      ...data,
      dependencyGroups: data.dependencyGroups.filter((_, i) => i !== index),
    });
  };

  const updateDependencyGroup = (
    index: number,
    updates: Partial<ServiceDependencyGroup>
  ) => {
    const updatedGroups = [...data.dependencyGroups];
    updatedGroups[index] = { ...updatedGroups[index], ...updates };
    setData({ ...data, dependencyGroups: updatedGroups });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Prepare request data - send dependencyGroups, filter out empty groups
    const requestData = {
      ...data,
      council_id: data.council_id!,
      dependencyGroups: data.dependencyGroups.filter(
        (group) => group.dependentServiceIds.length > 0
      ),
    };

    if (show?.action === "add") {
      createService({
        name: requestData.name,
        registrationFee: requestData.registrationFee,
        council_id: requestData.council_id,
        approvalFinalLevel: requestData.approvalFinalLevel,
        code: requestData.code,
        productCode: requestData.productCode,
        status: requestData.status,
        dependencyGroups: requestData.dependencyGroups,
        renewable: requestData.renewable,
        baseServiceId: requestData.baseServiceId || undefined,
      }, {
        onSuccess: () => {
          notifications.show({
            title: "Success",
            message: "Service Added Successfully",
            color: "green",
          });
          if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
            queryClient.invalidateQueries({
              queryKey: ["services", council?.id],
            })
          } else {
            queryClient.invalidateQueries({
              queryKey: ["services"],
            })
          }
          setShow(null);
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.response?.data?.message || "Failed to add service",
            color: "red",
          });
        }
      })
    } else if (show?.action === "edit" && show.service) {
      updateService({
        id: show.service.id,
        name: requestData.name,
        registrationFee: requestData.registrationFee,
        council_id: data.council_id || show.service.council_id,
        approvalFinalLevel: requestData.approvalFinalLevel,
        code: requestData.code,
        productCode: requestData.productCode,
        status: requestData.status,
        dependencyGroups: requestData.dependencyGroups,
        renewable: requestData.renewable,
        baseServiceId: requestData.baseServiceId || undefined,
      }, {
        onSuccess: () => {
          notifications.show({
            title: "Success",
            message: "Service Updated Successfully",
            color: "green",
          });
          if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
            queryClient.invalidateQueries({
              queryKey: ["services", council?.id],
            })
          } else {
            queryClient.invalidateQueries({
              queryKey: ["services"],
            })
          }
          setShow(null);
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.response?.data?.message || "Failed to update service",
            color: "red",
          });
        }
      })
    }
  };


  useEffect(() => {
    if (data.council_id) {
      fetchLevels(String(data.council_id));
    }
  }, [data.council_id]);

  useEffect(() => {
    if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
      setData({
        ...data,
        council_id: council?.id ?? null,
      });
    }
  }, [user]);

  useEffect(() => {
    if (show?.show && show.action === "edit" && show.service) {
      // Use dependencyGroups if available, otherwise convert from legacy dependentServices
      let dependencyGroups: ServiceDependencyGroup[] = [];
      
      if (show.service.dependencyGroups && show.service.dependencyGroups.length > 0) {
        dependencyGroups = show.service.dependencyGroups.map((group) => ({
          operator: group.operator || "AND",
          dependentServiceIds: group.dependentServiceIds || [],
        }));
      } else if (show.service.dependentServices && show.service.dependentServices.length > 0) {
        // Convert legacy format to new format
        dependencyGroups = convertLegacyDependencies(
          show.service.dependentServices.map((item) => item.id)
        );
      }

      setData({
        name: show.service.name,
        registrationFee: show.service.registrationFee,
        council_id: show.service.council_id,
        approvalFinalLevel: show.service.approvalFinalLevel,
        code: show.service.code,
        productCode: show.service.productCode || "",
        status: show.service.status,
        dependencyGroups,
        renewable: show.service.renewable ?? false,
        baseServiceId: show.service.baseServiceId ?? null,
      });
    } else if (show?.show && show.action === "add") {
      // Reset to empty state when adding new service
      // For ADMINCOUNCIL users, set council_id immediately
      const initialCouncilId = user?.roles?.[0]?.roleName === "ADMINCOUNCIL" 
        ? council?.id || null
        : null;
      
      setData({
        name: "",
        registrationFee: 0,
        council_id: initialCouncilId,
        approvalFinalLevel: 0,
        code: "",
        productCode: "",
        status: EServiceStatus.ACTIVE,
        dependencyGroups: [],
        renewable: false,
        baseServiceId: null,
      });
    }
  }, [show])

  return (
    <MainModal open={Boolean(show?.show)} onClose={() => setShow(null)} title={show?.action === "add" ? "Add Service" : "Update Service"}>
      <form onSubmit={onSubmit} className="flex flex-col gap-y-2">
        <CustomInput
          type="text"
          name="name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          label="Name"
          placeholder="Name"
          alwaysFocused
        />
        <CustomInput
          type="text"
          name="code"
          value={data.code}
          onChange={(e) => setData({ ...data, code: e.target.value })}
          label="Enter Code"
          placeholder="Please Choose valid Code"
          alwaysFocused
        />
        <CustomInput
          type="text"
          name="productCode"
          value={data.productCode}
          onChange={(e) => setData({ ...data, productCode: e.target.value })}
          label="Product Code"
          placeholder="Enter Product Code (optional)"
          description="Irembo product code for payment integration"
        />
        {
          user?.roles?.[0]?.roleName === "ADMIN" &&
          <CustomInput label="Select Your Council" alwaysFocused>
            <Select
              value={data.council_id ?? ""}
              onChange={(val) => setData({ ...data, council_id: val ?? null })}
              data={tblcouncils?.data.map((item) => ({
                value: item.id,
                label: item.councilName,
              }))}
              placeholder="Select Your Council"
              variant="unstyled"
              width="100%"
              className="w-full"
            />
          </CustomInput>
        }
        <CustomInput label="Select Final Structure Level" alwaysFocused>
          <Select
            value={String(data.approvalFinalLevel)}
            onChange={(val) =>
              setData({ ...data, approvalFinalLevel: Number(val) })
            }
            data={roleApproval.map((item) => ({
              value: item.value,
              label: item.label,
            }))}
            placeholder="Select Final Structure Level"
            variant="unstyled"
            width="100%"
            className="w-full"
            disabled={!data.council_id}
          />
        </CustomInput>
        <CustomInput
          type="number"
          name="amount"
          value={data.registrationFee}
          onChange={(e) => setData({ ...data, registrationFee: Number(e.target.value) })}
          label="Amount"
          placeholder="Enter Amount"
          alwaysFocused
        />
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Text size="sm" fw={500} mb={4}>
                Service Dependencies
              </Text>
              <Text size="xs" c="dimmed">
                Define prerequisites: services within a group use OR (any one), groups are combined with AND/OR
              </Text>
            </div>
            <Button
              size="sm"
              w={200}
              ml={10}
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={addDependencyGroup}
              disabled={!data.council_id}
            >
              Add Group
            </Button>
          </div>

          {data.dependencyGroups.length === 0 ? (
            <Paper p="lg" withBorder className="bg-gray-50">
              <Text size="sm" c="dimmed" ta="center">
                No dependency groups. Click "Add Group" to create prerequisites.
              </Text>
            </Paper>
          ) : (
            <Stack gap="lg">
              {data.dependencyGroups.map((group, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <div className="flex items-center justify-center gap-3 py-2">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <Paper p="xs" withBorder className="bg-blue-50">
                        <Group gap="xs" align="center">
                          <Text size="xs" fw={600} c="blue">
                            Combine with:
                          </Text>
                          <Select
                            value={group.operator}
                            onChange={(val) =>
                              updateDependencyGroup(index, {
                                operator: (val as DependencyOperator) || "AND",
                              })
                            }
                            data={[
                              { value: "AND", label: "AND (all required)" },
                              { value: "OR", label: "OR (any sufficient)" },
                            ]}
                            variant="filled"
                            size="xs"
                            className="min-w-[160px]"
                            styles={{
                              input: {
                                fontWeight: 600,
                                backgroundColor: group.operator === "OR" ? "#fef3c7" : "#dbeafe",
                                border: `1px solid ${group.operator === "OR" ? "#f59e0b" : "#3b82f6"}`,
                              },
                            }}
                          />
                        </Group>
                      </Paper>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                  )}
                  <Paper p="md" withBorder className="bg-white">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <div>
                          <Text size="sm" fw={600} mb={4}>
                            Group {index + 1}
                          </Text>
                          <Text size="xs" c="dimmed">
                            User must complete <strong>ANY</strong> service in this group
                          </Text>
                        </div>
                        {data.dependencyGroups.length > 1 && (
                          <ActionIcon
                            color="red"
                            variant="light"
                            onClick={() => removeDependencyGroup(index)}
                            size="lg"
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        )}
                      </Group>
                      <MultiSelect
                        value={group.dependentServiceIds}
                        onChange={(val) =>
                          updateDependencyGroup(index, {
                            dependentServiceIds: val ?? [],
                          })
                        }
                        data={
                          servicesByCouncil?.data
                            ?.filter(
                              (item) =>
                                !show?.service ||
                                item.id !== show.service.id
                            )
                            .map((item) => ({
                              value: item.id,
                              label: item.name,
                            })) || []
                        }
                        placeholder="Select services (user needs ANY one)"
                        variant="default"
                        className="w-full"
                        multiple
                        disabled={!data.council_id}
                        searchable
                        clearable
                      />
                      {group.dependentServiceIds.length > 0 && (
                        <Text size="xs" c="blue" mt={4}>
                          Selected: {group.dependentServiceIds.length} service{group.dependentServiceIds.length !== 1 ? "s" : ""}
                        </Text>
                      )}
                    </Stack>
                  </Paper>
                </React.Fragment>
              ))}
            </Stack>
          )}
        </div>
        <div className="mt-2">
          <Checkbox
            checked={data.renewable}
            onChange={(e) =>
              setData({
                ...data,
                renewable: e.currentTarget.checked,
                ...(e.currentTarget.checked ? {} : { baseServiceId: null }),
              })
            }
            label="Renewable Service"
            description="Allow multiple applications for this service (e.g., LICENSE RENEWAL)"
          />
        </div>
        {data.renewable && (
          <CustomInput label="Base service (for renewals)" alwaysFocused>
            <Select
              value={data.baseServiceId ?? ""}
              onChange={(val) => setData({ ...data, baseServiceId: val || null })}
              data={[
                { value: "", label: "None" },
                ...(servicesByCouncil?.data
                  ?.filter(
                    (item) =>
                      !show?.service || item.id !== show.service.id
                  )
                  .map((item) => ({
                    value: item.id,
                    label: item.name,
                  })) ?? []),
              ]}
              placeholder="Select base service (optional)"
              variant="unstyled"
              width="100%"
              className="w-full"
              disabled={!data.council_id}
              clearable
            />
          </CustomInput>
        )}
        <Button
          loading={creatingService || updatingService}
          disabled={
            creatingService ||
            updatingService ||
            !data.name ||
            !data.council_id ||
            !data.approvalFinalLevel ||
            data.registrationFee === undefined ||
            data.registrationFee === null ||
            isNaN(data.registrationFee)
          }
          type="submit"
          className="mt-4"
          size="lg"
        >
          {show?.action === "add" ? "Add Service" : "Update Service"}
        </Button>
      </form>
    </MainModal>
  );
};

export default AddUpdateService;
